import {AfterViewInit, ChangeDetectionStrategy, Component, Inject, signal} from '@angular/core';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {IsEndAfterStartDateParams, isEndAfterStartDateValidator, merchantUrlValidator} from '../../../core/pure-utils/validators';
import {addHours, addMinutes, format, getHours, getMinutes, isBefore, isEqual, startOfDay} from 'date-fns';
import {FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {distinctUntilChanged, filter, first, map, observeOn, shareReplay, startWith, switchMap, take} from 'rxjs/operators';
import {asyncScheduler, combineLatest, merge, of} from 'rxjs';
import {DashboardApiService} from '../../../core/services/api/dashboard-api.service';
import {MerchantApiService} from '../../../core/services/api/merchant-api.service';
import {AffiliateExclusiveOption, CreateMerchantDiscountRequest, EntityGroupName, MerchantDiscount, TagExclusiveOption} from '../../../core/dtos/api';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TitleCasePipe} from '@angular/common';
import {TagApiService} from '../../../core/services/api/tag-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DiscountApiService} from '../../../core/services/api/discount-api.service';
import {traverse} from '../../../core/pure-utils/object-utils';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {CreateDiscountResponse, createDiscountWidth, discountRuleTypeMap} from '../discount-management';
import {SnackBarService} from '../../../core-services/snack-bar/snack-bar.service';
import {HttpErrorResponse} from '@angular/common/http';
import {AreYouSureData, AreYouSureDialogComponent} from '../../../shared/are-you-sure-dialog/are-you-sure-dialog.component';
import {addDateAndTime, utcToMtTime} from '../../../core/pure-utils/string-utils';
import {MatSnackBarRef, SimpleSnackBar} from '@angular/material/snack-bar';
import {triggerHotJarGa} from '../../../core/pure-utils/analytics';
import {BreakpointObserver} from '@angular/cdk/layout';
import {globalUuid} from '../../../core/core-variables';
import {environment} from '../../../../environments/environment';

type DiscountRuleValue = 'none' | 'exclusive' | 'restricted' | 'qualified';
type ShopifyUpdateMerchantDiscountRequest = Omit<CreateMerchantDiscountRequest,
  'discount_name' | 'discount_description' | 'started_at' | 'expired_at' | 'discount_source'>;

@Component({
    templateUrl: './create-discount-dialog.component.html',
    styleUrls: ['./create-discount-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
            provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
        }],
    standalone: false
})
export class CreateDiscountDialogComponent implements AfterViewInit {
  readonly nowMtnTime = utcToMtTime(new Date());
  readonly affiliateOptionLimit = 100;
  readonly tagGroupOptionLimit = 10;
  readonly defaultTime = '00:00';
  readonly allAffiliateUUID = globalUuid;
  readonly allAffiliatesOption = <AffiliateExclusiveOption> {affiliate_name: 'All Affiliates', filterType: 'affiliates', affiliate_id: this.allAffiliateUUID};
  readonly noAffiliateOption = <AffiliateExclusiveOption> {affiliate_name: 'No Affiliates', filterType: 'affiliates', affiliate_id: null};
  readonly startingStartDate = this.data?.merchantDiscount?.started_at
    ? addMinutes(addHours(startOfDay(this.data.merchantDiscount.started_at), getHours(this.data.merchantDiscount.started_at)),
        getMinutes(this.data.merchantDiscount.started_at)) : null;
  readonly startingExpireDate = this.data?.merchantDiscount?.expired_at
    ? addMinutes(addHours(startOfDay(this.data.merchantDiscount.expired_at), getHours(this.data.merchantDiscount.expired_at)),
        getMinutes(this.data.merchantDiscount.expired_at)) : null;
  protected readonly environment = environment;
  public triggerAnalytics = triggerHotJarGa;

  backDropClick$Sub = this.matDialogRef.backdropClick().pipe(take(1))
    .subscribe(() => {
        this.matDialogRef.close({refreshTable: false, snackBarRef: this.errorSnackBarRef});
      }
    );

  isMerchantDefaultUrlLoaded = signal(false);
  readonly isEditMerchantDiscount = signal(!!this.data?.merchantDiscount);
  disableSubmitButton = signal(false);
  disableDeleteButton = signal(false);
  isMobile = signal(false);
  affiliateFilterPreviousInput: string | AffiliateExclusiveOption = '';
  errorSnackBarRef = null as MatSnackBarRef<SimpleSnackBar>;
  conflictingCouponCode = null;
  layoutChanges$Sub = this.breakpointObserver.observe(['only screen and (max-width: 479.98px)']).pipe(takeUntilDestroyed())
    .subscribe((breakpointState) => {
      this.matDialogRef.updateSize(breakpointState.matches ? 'unset' : createDiscountWidth);
      this.isMobile.set(breakpointState.matches);
    });
  currentMerchantAccount$ = this.dashboardApiService.currentAccount$.pipe(
    filter((currentAccount) => currentAccount.entity_group_name === 'merchants')
  );
  merchantUrls$ = this.currentMerchantAccount$.pipe(
    switchMap(currentAccount => this.merchantApiService.getMerchantWebsitesByEntityId(currentAccount.entity_id)),
    takeUntilDestroyed(),
    shareReplay(1)
  );

  discountDetailsFormControls = {
    titleInput: this.fb.control('', Validators.maxLength(255)),
    detailsInput: this.fb.control('', Validators.maxLength(255)),
    destinationUrlInput: this.fb.control('', [], [merchantUrlValidator(this.merchantUrls$)]),
  };
  discountDetailsFormGroup = this.fb.group(this.discountDetailsFormControls);

  readonly isEndAfterStartDateParams: IsEndAfterStartDateParams = {
    customMessage: 'End date must be after start date.',
    includeHoursAndMinutes: true,
    endDateFormControl: {name: 'endDatePicker', setErrorOnForm: false},
    endTimeFormControl: {name: 'endTimeInput', setErrorOnForm: true},
    startDateFormControl: {name: 'startDatePicker', setErrorOnForm: false},
    startTimeFormControl: {name: 'startTimeInput', setErrorOnForm: false}
  };
  discountScheduleFormControls = {
    startDatePicker: this.fb.control(null),
    startTimeInput: this.fb.control({value: null, disabled: true}),
    endDatePicker: this.fb.control(null),
    endTimeInput: this.fb.control({value: null, disabled: true})
  };
  discountScheduleFormGroup = this.fb.group(this.discountScheduleFormControls,
    {validators: [isEndAfterStartDateValidator(this.isEndAfterStartDateParams), this.isTimeInFuture('Start date must be in the future.')]});

  discountRulesFormControls = {
    couponCodeInput: this.fb.control('', [Validators.maxLength(45), this.isThereConflictingCode('Coupon code conflicts with existing coupon code.')]),
    ruleTypeSelect: this.fb.control<DiscountRuleValue>('none'),
    affiliateAutoComplete: this.fb.control<AffiliateExclusiveOption | TagExclusiveOption | string>({value: this.allAffiliatesOption, disabled: true})
  };
  discountRulesFormGroup = this.fb.group(this.discountRulesFormControls);

  shopifyFormControls = {
    titleInput: this.fb.control({value: '', disabled: true}),
    couponCodeInput: this.fb.control({value: '', disabled: true}),
    ruleTypeSelect: this.fb.control<DiscountRuleValue>('none'),
    destinationUrlInput: this.fb.control('', [], [merchantUrlValidator(this.merchantUrls$)]),
    affiliateAutoComplete: this.fb.control<AffiliateExclusiveOption | TagExclusiveOption | string>({value: this.allAffiliatesOption, disabled: true})
  };
  shopifyFormFormGroup = this.fb.group(this.shopifyFormControls);

  merchantDefaultWebsite$Sub = of(this.isEditMerchantDiscount()).pipe(
    filter(isEditMerchantDiscount => isEditMerchantDiscount === false),
    switchMap(() => this.merchantUrls$),
    map(websites => websites.filter(website => website.is_default))
  ).subscribe(website => {
    this.isMerchantDefaultUrlLoaded.set(true);
    this.discountDetailsFormControls.destinationUrlInput.setValue(website[0].merchant_website_url);
    this.discountDetailsFormControls.destinationUrlInput.markAsTouched();
    this.discountDetailsFormControls.destinationUrlInput.updateValueAndValidity();
  });
  endTime$Sub = this.discountScheduleFormControls.endDatePicker.valueChanges.pipe(takeUntilDestroyed())
    .subscribe(endDate => {
      if (endDate == null) {
        this.discountScheduleFormControls.endTimeInput.setValue(null);
        this.discountScheduleFormControls.endTimeInput.disable();
      } else if (this.discountScheduleFormControls.endTimeInput.value === null) {
        this.discountScheduleFormControls.endTimeInput.setValue(this.defaultTime);
        this.discountScheduleFormControls.endTimeInput.enable();
      } else {
        this.discountScheduleFormControls.endTimeInput.enable();
      }
    });
  startTime$Sub = this.discountScheduleFormControls.startDatePicker.valueChanges.pipe(takeUntilDestroyed())
    .subscribe(startDate => {
      if (startDate == null) {
        this.discountScheduleFormControls.startTimeInput.setValue(null);
        this.discountScheduleFormControls.startTimeInput.disable();
      } else if (this.discountScheduleFormControls.startTimeInput.value === null) {
        this.discountScheduleFormControls.startTimeInput.setValue(this.defaultTime);
        this.discountScheduleFormControls.startTimeInput.enable();
      } else {
        this.discountScheduleFormControls.startTimeInput.enable();
      }
    });
  disableRuleTypeField$Sub = merge(this.discountRulesFormControls.couponCodeInput.valueChanges, this.shopifyFormControls.couponCodeInput.valueChanges).pipe(
    startWith(this.data?.merchantDiscount?.coupon_code ?? ''), takeUntilDestroyed()
  ).subscribe(coupon => {
    if (this.data.merchantDiscount && this.data.merchantDiscount.discount_source === 'shopify') {
      if (coupon === null || coupon === '') {
        this.shopifyFormControls.ruleTypeSelect.setValue('none');
        this.shopifyFormControls.ruleTypeSelect.disable();
      } else {
        this.shopifyFormControls.ruleTypeSelect.enable();
      }
    } else {
      if (coupon === '') {
        this.discountRulesFormControls.ruleTypeSelect.setValue('none');
        this.discountRulesFormControls.ruleTypeSelect.disable();
      } else {
        this.discountRulesFormControls.ruleTypeSelect.enable();
      }
    }
  });
  updateAffiliateAutoComplete$Sub = merge(this.discountRulesFormControls.ruleTypeSelect.valueChanges, this.shopifyFormControls.ruleTypeSelect.valueChanges).pipe(
    startWith(this.data.merchantDiscount?.discount_rule_type?.discount_rule_type_name ?? 'none'), distinctUntilChanged(), takeUntilDestroyed())
    .subscribe(ruleType => {
      let lowerRuleType = ruleType.toLowerCase();
      if (this.data.merchantDiscount && this.data.merchantDiscount.discount_source === 'shopify') {
        if (lowerRuleType === 'restricted') {
          this.shopifyFormControls.affiliateAutoComplete.setValue(this.allAffiliatesOption);
          this.shopifyFormControls.affiliateAutoComplete.removeValidators(Validators.required);
          this.shopifyFormControls.affiliateAutoComplete.updateValueAndValidity();
          this.shopifyFormControls.affiliateAutoComplete.disable();
        } else {
          this.shopifyFormControls.affiliateAutoComplete.setValue(lowerRuleType === 'none' ? this.allAffiliatesOption : '');
          this.shopifyFormControls.affiliateAutoComplete.addValidators(Validators.required);
          this.shopifyFormControls.affiliateAutoComplete.updateValueAndValidity();
          this.affiliateFilterPreviousInput = '';
          this.shopifyFormControls.affiliateAutoComplete.enable();
        }
      } else {
        if (lowerRuleType === 'restricted') {
          this.discountRulesFormControls.affiliateAutoComplete.setValue(this.allAffiliatesOption);
          this.discountRulesFormControls.affiliateAutoComplete.removeValidators(Validators.required);
          this.discountRulesFormControls.affiliateAutoComplete.updateValueAndValidity();
          this.discountRulesFormControls.affiliateAutoComplete.disable();
        } else {
          this.discountRulesFormControls.affiliateAutoComplete.setValue(lowerRuleType === 'none' ? this.allAffiliatesOption : '');
          this.discountRulesFormControls.affiliateAutoComplete.addValidators(Validators.required);
          this.discountRulesFormControls.affiliateAutoComplete.updateValueAndValidity();
          this.affiliateFilterPreviousInput = '';
          this.discountRulesFormControls.affiliateAutoComplete.enable();
        }
      }
    });

  usersAffiliates$ = this.dashboardApiService.usersAffiliates$;
  affiliateFilteredOptions$ = combineLatest([
    merge(this.discountRulesFormControls.affiliateAutoComplete.valueChanges, this.shopifyFormControls.affiliateAutoComplete.valueChanges),
      this.usersAffiliates$]).pipe(
    map(([affiliateFilter, affiliateOptions]) => {
      if (affiliateFilter === '' || typeof affiliateFilter !== 'string') {
        return affiliateOptions;
      }

      let lowerInput = affiliateFilter.toLowerCase();
      return affiliateOptions.filter(x => x.affiliate_name.toLowerCase().indexOf(lowerInput) !== -1 ||
        x.affiliate_website_url_protocol_stripped.indexOf(lowerInput) !== -1 ||
        String(x.classic_affiliate_id).indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  affiliateFilteredOptionsLimited$ = this.affiliateFilteredOptions$.pipe(
    map(x => x.length > this.affiliateOptionLimit ? x.slice(0, this.affiliateOptionLimit) : x));

  // The displayWith requires we use arrow syntax, so we can access the titleCasePipe class property.
  affiliateFilterDisplayWith = (value: AffiliateExclusiveOption | TagExclusiveOption): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value?.filterType === 'affiliates') {
      return value.affiliate_name.trim();
    }
    if (value?.filterType === 'tag_groups') {
      return this.titleCasePipe.transform(`${value.tag_name.trim()} Tag Group`);
    }
    return value;
  };

  usersTagGroups$ = this.tagApiService.usersTagGroups$;
  userTagFilteredOptions$ = combineLatest([this.discountRulesFormControls.affiliateAutoComplete.valueChanges, this.usersTagGroups$]).pipe(
    map(([exclusiveFilter, tagOptions]) => {
      if (exclusiveFilter === '' || typeof exclusiveFilter !== 'string') {
        return tagOptions;
      }

      let lowerInput = exclusiveFilter.toLowerCase();
      return tagOptions.filter(x => x.tag_name.toLowerCase().indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  userTagFilteredOptionsLimited$ = this.userTagFilteredOptions$.pipe(
    map(x => x.length > this.tagGroupOptionLimit ? x.slice(0, this.tagGroupOptionLimit) : x));

  setFormFieldsByEditableDiscount$Sub = of(this.isEditMerchantDiscount()).pipe(
    filter(isEditMerchantDiscount => isEditMerchantDiscount === true),
    switchMap(() => combineLatest([of(this.data.merchantDiscount), this.usersAffiliates$, this.usersTagGroups$])),
    takeUntilDestroyed()
  ).subscribe(([merchantDiscount, usersAffiliates, usersTagGroups]) => {
    if (merchantDiscount.discount_source === 'shopify') {
      this.shopifyFormControls.titleInput.setValue(merchantDiscount.discount_name);
      this.shopifyFormControls.couponCodeInput.setValue(merchantDiscount.coupon_code);
      this.shopifyFormControls.destinationUrlInput.setValue(merchantDiscount.discount_url);
      this.isMerchantDefaultUrlLoaded.set(true);
      if (merchantDiscount?.discount_rule_type?.discount_rule_type_name) {
        this.shopifyFormControls.ruleTypeSelect.setValue(
          merchantDiscount?.discount_rule_type?.discount_rule_type_name.toString().toLowerCase() as DiscountRuleValue);
      }
      let discountEntity: AffiliateExclusiveOption | TagExclusiveOption;
      if (merchantDiscount.discount_entity_id !== null && merchantDiscount.discount_entity_id !== this.allAffiliateUUID) {
        if (merchantDiscount.discount_entity_group_name === 'affiliates') {
          discountEntity = usersAffiliates.find(affiliate => affiliate.affiliate_id === merchantDiscount.discount_entity_id);
        } else {
          discountEntity = usersTagGroups.find(tagGroup => tagGroup.tag_id === merchantDiscount.discount_entity_id);
        }
        this.shopifyFormControls.affiliateAutoComplete.setValue(discountEntity === null ? '' : discountEntity);
      } else {
        discountEntity = merchantDiscount.discount_entity_id === this.allAffiliateUUID ? this.allAffiliatesOption : this.noAffiliateOption;
      }
      this.shopifyFormControls.affiliateAutoComplete.setValue(discountEntity);
      this.shopifyFormFormGroup.markAllAsTouched();
      this.shopifyFormFormGroup.updateValueAndValidity();
    } else {
      this.discountDetailsFormControls.titleInput.setValue(merchantDiscount.discount_name);
      this.discountDetailsFormControls.detailsInput.setValue(merchantDiscount.discount_description);
      this.discountDetailsFormControls.destinationUrlInput.setValue(merchantDiscount.discount_url);
      this.isMerchantDefaultUrlLoaded.set(true);
      this.discountDetailsFormGroup.markAllAsTouched();
      this.discountDetailsFormGroup.updateValueAndValidity();

      let startDate = new Date(merchantDiscount.started_at);
      let startHourAndMinute = format(startDate, 'HH:mm');
      let endDate = merchantDiscount.expired_at ? new Date(merchantDiscount.expired_at) : null;
      let endHourAndMinute = endDate ? format(endDate, 'HH:mm') : null;
      this.discountScheduleFormControls.startDatePicker.setValue(startOfDay(startDate));
      this.discountScheduleFormControls.startTimeInput.setValue(startHourAndMinute);
      if (endDate !== null) {
        this.discountScheduleFormControls.endDatePicker.setValue(startOfDay(endDate));
        this.discountScheduleFormControls.endTimeInput.setValue(endHourAndMinute);
      }
      this.discountScheduleFormGroup.markAllAsTouched();
      this.discountScheduleFormGroup.updateValueAndValidity();

      this.discountRulesFormControls.couponCodeInput.setValue(merchantDiscount.coupon_code === null ? '' : merchantDiscount.coupon_code);
      if (merchantDiscount?.discount_rule_type?.discount_rule_type_name) {
        this.discountRulesFormControls.ruleTypeSelect.setValue(
          merchantDiscount?.discount_rule_type?.discount_rule_type_name.toString().toLowerCase() as DiscountRuleValue);
      }
      let discountEntity: AffiliateExclusiveOption | TagExclusiveOption;
      if (merchantDiscount.discount_entity_id !== null && merchantDiscount.discount_entity_id !== this.allAffiliateUUID) {
        if (merchantDiscount.discount_entity_group_name === 'affiliates') {
          discountEntity = usersAffiliates.find(affiliate => affiliate.affiliate_id === merchantDiscount.discount_entity_id);
        } else {
          discountEntity = usersTagGroups.find(tagGroup => tagGroup.tag_id === merchantDiscount.discount_entity_id);
        }
        this.shopifyFormControls.affiliateAutoComplete.setValue(discountEntity === null ? '' : discountEntity);
      } else {
        discountEntity = merchantDiscount.discount_entity_id === this.allAffiliateUUID ? this.allAffiliatesOption : this.noAffiliateOption;
      }
      this.discountRulesFormControls.affiliateAutoComplete.setValue(discountEntity);
      this.discountRulesFormGroup.markAllAsTouched();
      this.discountRulesFormGroup.updateValueAndValidity();
    }
  });

  onSelectedAffiliateFilterAutoComplete($event: MatAutocompleteSelectedEvent, filteredAffiliateInput: HTMLInputElement) {
    this.affiliateFilterPreviousInput = $event.option.value;
    filteredAffiliateInput.blur();
  }
  onOpenAutoSelect = (formControl: FormControl) => formControl.setValue('');
  onCloseAffiliateFilterAutoComplete() {
    if (typeof this.discountRulesFormControls.affiliateAutoComplete.value === 'string') {
      this.discountRulesFormControls.affiliateAutoComplete.setValue(this.affiliateFilterPreviousInput);
    }
  }
  onSaveButtonClick() {
    this.disableSubmitButton.set(true);
    this.currentMerchantAccount$.pipe(
      switchMap((currentAccount) => {
        if (this.data.merchantDiscount && this.data.merchantDiscount.discount_source === 'shopify') {
          let discount_entity_group_name =
            ((this.shopifyFormControls.affiliateAutoComplete.value as AffiliateExclusiveOption | TagExclusiveOption).filterType as EntityGroupName | 'tag_groups');
          let discount_entity_id: string;
          if (discount_entity_group_name === 'affiliates') {
            discount_entity_id = (this.shopifyFormControls.affiliateAutoComplete.value as AffiliateExclusiveOption).affiliate_id;
            if (this.shopifyFormControls.ruleTypeSelect.value === 'none' && discount_entity_id === null) {
              discount_entity_group_name = null;
            }
          }
          if (discount_entity_group_name === 'tag_groups') {
            discount_entity_id = (this.shopifyFormControls.affiliateAutoComplete.value as TagExclusiveOption).tag_id;
          }
          let updateShopifyMerchantDiscountRequest: ShopifyUpdateMerchantDiscountRequest = {
            discount_entity_group_name,
            discount_entity_id,
            discount_rule_type_id: this.shopifyFormControls.ruleTypeSelect.value !== 'none'
              ? discountRuleTypeMap[this.shopifyFormControls.ruleTypeSelect.value] : null,
            discount_url: this.shopifyFormControls.destinationUrlInput.value,
            coupon_code: this.shopifyFormControls.couponCodeInput.value
          };
          return this.discountApiService.updateMerchantDiscount(
            currentAccount.entity_id,
            this.data.merchantDiscount.discount_id,
            updateShopifyMerchantDiscountRequest
          );
        } else {
          this.conflictingCouponCode = null;
          let formStartTime = this.discountScheduleFormControls.startDatePicker.value
            ? addDateAndTime(startOfDay(this.discountScheduleFormControls.startDatePicker.value), this.discountScheduleFormControls.startTimeInput.value) : null;
          let formEndTime = this.discountScheduleFormControls.endDatePicker.value
            ? addDateAndTime(startOfDay(this.discountScheduleFormControls.endDatePicker.value), this.discountScheduleFormControls.endTimeInput.value) : null;
          let currentMtnTimeAtSubmission = utcToMtTime(new Date());
          const started_at = formStartTime === null || (isBefore(formStartTime, currentMtnTimeAtSubmission) && !isEqual(formStartTime, this.startingStartDate))
            ? 'now' : formStartTime;
          const expired_at = (formEndTime !== null) && (isBefore(formEndTime, currentMtnTimeAtSubmission) && !isEqual(formEndTime, this.startingStartDate))
            ? 'now' : formEndTime;
          let discount_entity_group_name =
            ((this.discountRulesFormControls.affiliateAutoComplete.value as AffiliateExclusiveOption | TagExclusiveOption)
              .filterType as EntityGroupName | 'tag_groups');
          let discount_entity_id: string;
          if (discount_entity_group_name === 'affiliates') {
            discount_entity_id = (this.discountRulesFormControls.affiliateAutoComplete.value as AffiliateExclusiveOption).affiliate_id;
            if (this.discountRulesFormControls.ruleTypeSelect.value === 'none' && discount_entity_id === null) {
              discount_entity_group_name = null;
            }
          }
          if (discount_entity_group_name === 'tag_groups') {
            discount_entity_id = (this.discountRulesFormControls.affiliateAutoComplete.value as TagExclusiveOption).tag_id;
          }
          let createMerchantDiscountRequest: CreateMerchantDiscountRequest = {
            coupon_code: this.discountRulesFormControls.couponCodeInput.value === '' ? null : this.discountRulesFormControls.couponCodeInput.value,
            discount_description: this.discountDetailsFormControls.detailsInput.value,
            discount_entity_group_name,
            discount_entity_id,
            discount_name: this.discountDetailsFormControls.titleInput.value,
            discount_rule_type_id: this.discountRulesFormControls.ruleTypeSelect.value !== 'none'
              ? discountRuleTypeMap[this.discountRulesFormControls.ruleTypeSelect.value] : null,
            discount_url: this.discountDetailsFormControls.destinationUrlInput.value,
            discount_source: 'avantlink',
            expired_at,
            started_at
          };

          if (this.isEditMerchantDiscount()) {
            // If expired_at or started_at haven't changed on an edit, delete those properties when updating.
            if (expired_at !== 'now' && expired_at?.toISOString() === this.startingExpireDate?.toISOString()) {
              delete createMerchantDiscountRequest.expired_at;
            }
            if (started_at !== 'now' && (started_at?.toISOString() === this.startingStartDate?.toISOString())) {
              delete createMerchantDiscountRequest.started_at;
            }
            return this.discountApiService.updateMerchantDiscount(currentAccount.entity_id, this.data.merchantDiscount.discount_id, createMerchantDiscountRequest);
          }
          // delete all null properties from request query;
          traverse(createMerchantDiscountRequest, (currentObject, propName, value) => {
            if (value === null || value === '') {
              delete currentObject[propName];
            }
          });
          return this.discountApiService.createMerchantDiscount(currentAccount.entity_id, createMerchantDiscountRequest as CreateMerchantDiscountRequest);
        }
      }),
      first()
    ).subscribe({
      next: success => {
        this.snackBarService.openSnackBar('Discount Saved');
        this.matDialogRef.close({snackBarRef: this.errorSnackBarRef, refreshTable: true});
      },
      error: (error: HttpErrorResponse) => {
        this.disableSubmitButton.set(false);
        // snack closes after 1 minute.
        this.errorSnackBarRef = this.snackBarService.openErrorSnackBar(error.error.error, undefined, 60000);
        if (error.error.error_data === 'coupon') {
          this.conflictingCouponCode = this.discountRulesFormControls.couponCodeInput.value;
          this.discountRulesFormControls.couponCodeInput.updateValueAndValidity();
        }
      }
    });
  }
  onDeleteButtonClick() {
    this.disableDeleteButton.set(true);
    let matDialogRef = this.matDialog.open<AreYouSureDialogComponent, AreYouSureData, boolean>(
      AreYouSureDialogComponent,
      {data: {
          header: 'Delete Discount',
          message: 'Are you sure you want to delete this discount?',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Delete'
        } as AreYouSureData
      });

    let onClosed$Sub = matDialogRef.afterClosed().pipe(
      filter(confirmDelete => confirmDelete === true),
      switchMap(() => combineLatest([this.currentMerchantAccount$, of(this.data.merchantDiscount)])),
      switchMap(([currentMerchantAccount, merchantDiscount]) =>
        this.discountApiService.deleteMerchantDiscount(currentMerchantAccount.entity_id, merchantDiscount.discount_id)),
      take(1),
    ).subscribe({
      next: success => {
        this.snackBarService.openSnackBar('Discount Deleted');
        this.matDialogRef.close({snackBarRef: this.errorSnackBarRef, refreshTable: true});
      },
      error: (error: HttpErrorResponse) => {
        this.errorSnackBarRef = this.snackBarService.openErrorSnackBar(error.error.error);
      },
      complete: () => {
        this.disableDeleteButton.set(false);
      }
    });
  }

  isTimeInFuture(customMessage: string): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      let startDate = formGroup.get('startDatePicker').value ? startOfDay(formGroup.get('startDatePicker').value) : null;
      let startTime = formGroup.get('startTimeInput').value;
      let endDate = formGroup.get('endDatePicker').value ? startOfDay(formGroup.get('endDatePicker').value) : null;
      let endTime = formGroup.get('endTimeInput').value;

      if ((startDate === null || startTime === null) && (endDate === null || endTime === null)) {
        return null;
      }
      if (startDate || startTime) {
        // If start date and time have an error return that and ignore any end date and time errors. Otherwise,
        // do not return and check end date and time for errors.
        if (isBefore(addDateAndTime(startDate, startTime), this.nowMtnTime)
          && (!this.isEditMerchantDiscount() || !isEqual(addDateAndTime(startDate, startTime), this.startingStartDate))) {
          formGroup.get('startTimeInput').setErrors({isTimeInFuture: {customMessage}});
          return {isTimeInFuture: {customMessage}};
        } else {
          let updatedErrors = formGroup.get('startTimeInput').errors;
          if (updatedErrors?.hasOwnProperty('isTimeInFuture')) {
            delete updatedErrors.isTimeInFuture;
          }
          formGroup.get('startTimeInput').setErrors(updatedErrors);
        }
      }
      if (endDate || endTime) {
        if (isBefore(addDateAndTime(endDate, endTime), this.nowMtnTime)
          && (!this.isEditMerchantDiscount() || !isEqual(addDateAndTime(endDate, endTime), this.startingExpireDate))) {
          formGroup.get('endTimeInput').setErrors({isTimeInFuture: {customMessage}});
          return {isTimeInFuture: {customMessage}};
        } else {
          let updatedErrors = formGroup.get('endTimeInput').errors;
          if (updatedErrors?.hasOwnProperty('isTimeInFuture')) {
            delete updatedErrors.isTimeInFuture;
          }
          formGroup.get('endTimeInput').setErrors(updatedErrors);
        }
      }
      return null;
    };
  }
  isThereConflictingCode(customMessage: string): ValidatorFn {
    return (formControl) => {
      if (this.conflictingCouponCode === null || this.conflictingCouponCode !== formControl.value) {
        return null;
      }
      return {isThereConflictingCode: {customMessage}};
    };
  }
  startDateValidation = (date: Date | null): boolean => {
    if (this.data?.merchantDiscount) {
      return !isBefore(startOfDay(date), startOfDay(this.nowMtnTime)) || isEqual(startOfDay(date), startOfDay(new Date(this.data.merchantDiscount.started_at)));
    }
    return !isBefore(startOfDay(date), startOfDay(this.nowMtnTime));
  };

  constructor(private fb: FormBuilder, private dashboardApiService: DashboardApiService, private merchantApiService: MerchantApiService,
              private titleCasePipe: TitleCasePipe, private tagApiService: TagApiService, private discountApiService: DiscountApiService,
              private matDialogRef: MatDialogRef<CreateDiscountDialogComponent, CreateDiscountResponse>, private snackBarService: SnackBarService,
              @Inject(MAT_DIALOG_DATA) protected data: {merchantDiscount: MerchantDiscount}, private matDialog: MatDialog,
              private breakpointObserver: BreakpointObserver) {
  }
  ngAfterViewInit(): void {
    this.discountScheduleFormControls.endTimeInput.markAsTouched();
    this.discountScheduleFormControls.startTimeInput.markAsTouched();
  }
}
